import type { TokenizePaymentMethodFormData } from "@/store/profile/forms/useTokenizePaymentMethodForm";
import { useTokenizePaymentMethodForm } from "@/store/profile/forms/useTokenizePaymentMethodForm";
import { useTokenizePaymentMethodStore } from "@/store/profile/tokenizePaymentMethod";
import { getErrorMessage } from "@/lib/helpers/string";
import { showToast } from "@/components/ui/toast/show-toast";
import { useCreatePaymentMethodStore } from "@/store/profile/createPaymentMethod";
import { useAuthState } from "@/store/auth";
import { useDeletePaymentMethodStore } from "@/store/profile/deletePaymentMethod";
import { useUpdateClientPreferencesStore } from "@/store/profile/updateClientPreferences";
import { getExpirationsRequestInput } from "../utils";
import type { Control } from "react-hook-form";
import { useRoute } from "@/components/shared/router";
import { AddCreditCardScreen } from "./add-credit-card-screen";
import { useInvoiceState } from "@/store/invoices";
import { PaymentMethodScreenModal } from "@/types/invoice";
import { useClientPaymentMethodStore } from "@/store/auth/clientPaymentMethod";

export type AddCreditCardScreenProps = {
  loading: boolean;
  onConfirm: () => Promise<void>;
  control: Control<TokenizePaymentMethodFormData>;
  infoCardDesc: string;
};

export const AddCreditCard = () => {
  const { push } = useRoute();

  const {
    data: { userInfo },
  } = useAuthState();

  const {
    data: { addCreditCardScreenModal },
    showPaymentMethodModal,
  } = useInvoiceState();

  const fetchClientPaymentMethod = useClientPaymentMethodStore(
    (state) => state.fetch,
  );
  const {
    fetch: tokenizePaymentMethod,
    loading: tokenizePaymentMethodLoading,
  } = useTokenizePaymentMethodStore();
  const { fetch: createPaymentMethod, loading: createPaymentMethodLoading } =
    useCreatePaymentMethodStore();
  const { fetch: deletePaymentMethod, loading: deletePaymentMethodLoading } =
    useDeletePaymentMethodStore();
  const {
    fetch: updateClientPreferences,
    loading: updateClientPreferencesLoading,
  } = useUpdateClientPreferencesStore();

  const { control, handleSubmit } = useTokenizePaymentMethodForm();

  const paymentMethod = userInfo.paymentMethod;

  const onDeleteCard = async () => {
    if (paymentMethod) {
      try {
        const deleteRes = await deletePaymentMethod(
          {
            requestPayload: {
              input: {
                clientId: userInfo.id,
                paymentMethodId: paymentMethod.id,
              },
            },
          },
          { selfHandleError: true },
        );

        if (deleteRes.error) {
          throw new Error(deleteRes.error);
        }

        const updatePreferences = await updateClientPreferences(
          {
            requestPayload: {
              input: {
                clientId: userInfo.id,
                preferences: {
                  autoPay: false,
                },
              },
            },
          },
          { selfHandleError: true },
        );

        if (updatePreferences.error) {
          throw new Error(updatePreferences.error);
        }

        fetchClientPaymentMethod({
          requestPayload: {
            id: userInfo.id,
          },
        });
      } catch (error) {
        const errMsg = getErrorMessage(error, "Error deleting payment method");
        showToast({
          title: errMsg,
          type: "error",
        });
      }
    }
  };

  const onConfirm = handleSubmit(
    async (data: TokenizePaymentMethodFormData) => {
      const { expMonth, expYear } = getExpirationsRequestInput(data.expDate);
      try {
        const tokenizeRes = await tokenizePaymentMethod(
          {
            requestPayload: {
              input: {
                name: data.name,
                cvc: data.cvc,
                number: data.number,
                expMonth,
                expYear,
              },
            },
          },
          { selfHandleError: true },
        );

        if (tokenizeRes.error) {
          throw new Error(tokenizeRes.error);
        }

        if (tokenizeRes.data?.token) {
          await onDeleteCard();
          try {
            const createPaymentRes = await createPaymentMethod(
              {
                requestPayload: {
                  input: {
                    clientId: userInfo.id,
                    token: tokenizeRes.data.token,
                  },
                },
              },
              { selfHandleError: true },
            );

            fetchClientPaymentMethod({
              requestPayload: {
                id: userInfo.id,
              },
            });

            if (createPaymentRes.error) {
              throw new Error(createPaymentRes.error);
            }
            if (addCreditCardScreenModal) {
              showPaymentMethodModal(PaymentMethodScreenModal.CREDIT_CARD);
            } else {
              push({ pageKey: "payment" });
            }
          } catch (createPaymentError) {
            const errMsg = getErrorMessage(
              createPaymentError,
              "Error creating payment method",
            );
            showToast({
              title: errMsg,
              type: "error",
            });
          }
        }
      } catch (error) {
        const errMsg = getErrorMessage(
          error,
          "Error tokenizing payment method",
        );
        showToast({
          title: errMsg,
          type: "error",
        });
      }
    },
  );

  const loading =
    tokenizePaymentMethodLoading ||
    createPaymentMethodLoading ||
    deletePaymentMethodLoading ||
    updateClientPreferencesLoading;

  const infoCardDesc = paymentMethod
    ? "We will automatically charge your card on file after the due date of your invoice. A processing fee charged by the credit card processor of 3.4% + 50 cents will be added to the bill. Alternatively, you can manually pay via PayNow directly with the invoice which does not have a processing fee."
    : "We impose a surcharge of 3.4% + $0.5 for Debit & Credit Cards only, which goes towards card processing costs and is not greater than our cost of acceptance. Unfortunately, we currently don't accept Visa, but you can proceed with other payment networks.";
  return (
    <AddCreditCardScreen
      loading={loading}
      onConfirm={onConfirm}
      control={control}
      infoCardDesc={infoCardDesc}
    />
  );
};
